import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { contentApi } from '@/services/api';
import { publicApi } from '@/services/publicApi';
import { cmsPages, type CMSPage, type PageSection, type PageSEO } from '@/mocks/contentManagement';

interface ContentContextValue {
  /** All loaded pages keyed by pageKey */
  pages: Record<string, CMSPage>;
  /** Whether initial load is in progress */
  loading: boolean;
  /** Error message if load failed */
  error: string | null;
  /** Get content for a specific page */
  getPage: (pageKey: string) => CMSPage | undefined;
  /** Get sections for a specific page */
  getSections: (pageKey: string) => PageSection[];
  /** Get SEO for a specific page */
  getSEO: (pageKey: string) => PageSEO | undefined;
  /** Update a single section (optimistic — updates local + calls API) */
  saveSection: (pageKey: string, section: PageSection) => Promise<void>;
  /** Bulk update all sections for a page */
  savePage: (pageKey: string, sections: PageSection[]) => Promise<void>;
  /** Mark a section as dirty (local-only, for tracking unsaved changes) */
  markDirty: (pageKey: string, sectionId: string, content: string) => void;
  /** Discard local changes for a section */
  discardChanges: (pageKey: string, sectionId: string) => void;
  /** Get dirty state for a section */
  isDirty: (pageKey: string, sectionId: string) => boolean;
  /** Refresh content from API */
  refresh: () => Promise<void>;
}

const ContentContext = createContext<ContentContextValue | null>(null);

/** Map of dirty sections: `${pageKey}::${sectionId}` → new content */
type DirtyMap = Record<string, string>;

export function ContentProvider({ children }: { children: React.ReactNode }) {
  const [pages, setPages] = useState<Record<string, CMSPage>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dirtyMap, setDirtyMap] = useState<DirtyMap>({});
  // Track original (server) state for revert
  const serverPagesRef = useRef<Record<string, CMSPage>>({});

  // Build pages map from array (deep-clone so we can mutate sections)
  const buildPagesMap = useCallback((pageList: CMSPage[]): Record<string, CMSPage> => {
    const map: Record<string, CMSPage> = {};
    for (const p of pageList) {
      map[p.pageKey] = JSON.parse(JSON.stringify(p));
    }
    return map;
  }, []);

  // Load content: mock data is the base (structure + fallback content),
  // then we overlay real content from the PUBLIC API (no auth needed)
  const loadContent = useCallback(async () => {
    setLoading(true);
    setError(null);

    // Step 1: Set mock data immediately as the base (instant render)
    const mockMap = buildPagesMap(cmsPages);
    setPages(mockMap);
    serverPagesRef.current = JSON.parse(JSON.stringify(mockMap));
    setDirtyMap({});

    // Step 2: Try public API to overlay real CMS content
    try {
      const res = await publicApi.getContent();
      if (res.data?.pages && Array.isArray(res.data.pages) && res.data.pages.length > 0) {
        // Clone the current mock map so we can overlay content
        const mergedMap = buildPagesMap(cmsPages);

        for (const apiPage of res.data.pages) {
          const mockPage = mergedMap[apiPage.page];
          if (!mockPage || !apiPage.sections) continue;

          for (const apiSec of apiPage.sections) {
            const mockSection = mockPage.sections.find(s => s.id === apiSec.section);
            if (mockSection) {
              // Override content from database
              let content = apiSec.data;
              try {
                // data might be JSON-stringified; try unwrapping
                const parsed = JSON.parse(apiSec.data);
                if (typeof parsed === 'string') content = parsed;
              } catch {
                // Not JSON — use the raw string
                content = apiSec.data;
              }
              mockSection.content = content;
              if (apiSec.updated_at) {
                mockSection.lastModified = apiSec.updated_at.slice(0, 10);
              }
            }
          }
        }

        setPages(mergedMap);
        serverPagesRef.current = JSON.parse(JSON.stringify(mergedMap));
        setDirtyMap({});
      }
    } catch {
      // Public API not available — mock data is already set, nothing to do
    }

    setLoading(false);
  }, [buildPagesMap]);

  useEffect(() => {
    loadContent();
  }, [loadContent]);

  const getPage = useCallback((pageKey: string) => pages[pageKey], [pages]);

  const getSections = useCallback((pageKey: string): PageSection[] => {
    const page = pages[pageKey];
    if (!page) return [];
    return page.sections.map((s) => {
      const dirtyKey = `${pageKey}::${s.id}`;
      if (dirtyMap[dirtyKey] !== undefined) {
        return { ...s, content: dirtyMap[dirtyKey] };
      }
      return s;
    });
  }, [pages, dirtyMap]);

  const getSEO = useCallback((pageKey: string): PageSEO | undefined => {
    return pages[pageKey]?.seo;
  }, [pages]);

  const saveSection = useCallback(async (pageKey: string, section: PageSection) => {
    const dirtyKey = `${pageKey}::${section.id}`;
    const newContent = dirtyMap[dirtyKey] ?? section.content;

    // Optimistic update
    setPages((prev) => {
      const page = prev[pageKey];
      if (!page) return prev;
      return {
        ...prev,
        [pageKey]: {
          ...page,
          sections: page.sections.map((s) =>
            s.id === section.id
              ? { ...s, content: newContent, lastModified: new Date().toISOString().slice(0, 10) }
              : s
          ),
        },
      };
    });

    // Clear dirty state
    setDirtyMap((prev) => {
      const next = { ...prev };
      delete next[dirtyKey];
      return next;
    });

    // Persist to API (fire-and-forget — auth required, works from dashboard)
    try {
      await contentApi.save(pageKey, section.id, newContent);
    } catch {
      // Silently fail — optimistic update keeps the UI correct
    }
  }, [dirtyMap]);

  const savePage = useCallback(async (pageKey: string, sections: PageSection[]) => {
    // Build the sections payload from dirty state + current content
    const payload: Record<string, unknown> = {};
    for (const s of sections) {
      const dirtyKey = `${pageKey}::${s.id}`;
      payload[s.id] = dirtyMap[dirtyKey] ?? s.content;
    }

    // Optimistic update
    setPages((prev) => {
      const page = prev[pageKey];
      if (!page) return prev;
      const today = new Date().toISOString().slice(0, 10);
      return {
        ...prev,
        [pageKey]: {
          ...page,
          sections: sections.map((s) => {
            const dirtyKey = `${pageKey}::${s.id}`;
            const content = dirtyMap[dirtyKey] ?? s.content;
            return { ...s, content, lastModified: today };
          }),
        },
      };
    });

    // Clear all dirty state for this page
    setDirtyMap((prev) => {
      const next = { ...prev };
      for (const s of sections) {
        delete next[`${pageKey}::${s.id}`];
      }
      return next;
    });

    // Persist to API
    try {
      await contentApi.bulkUpdate(pageKey, payload);
    } catch {
      // Silently fail
    }
  }, [dirtyMap]);

  const markDirty = useCallback((pageKey: string, sectionId: string, content: string) => {
    const key = `${pageKey}::${sectionId}`;
    setDirtyMap((prev) => ({ ...prev, [key]: content }));
  }, []);

  const discardChanges = useCallback((pageKey: string, sectionId: string) => {
    const key = `${pageKey}::${sectionId}`;
    setDirtyMap((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const isDirty = useCallback((pageKey: string, sectionId: string) => {
    return dirtyMap[`${pageKey}::${sectionId}`] !== undefined;
  }, [dirtyMap]);

  const value = useMemo<ContentContextValue>(() => ({
    pages,
    loading,
    error,
    getPage,
    getSections,
    getSEO,
    saveSection,
    savePage,
    markDirty,
    discardChanges,
    isDirty,
    refresh: loadContent,
  }), [pages, loading, error, getPage, getSections, getSEO, saveSection, savePage, markDirty, discardChanges, isDirty, loadContent]);

  return (
    <ContentContext.Provider value={value}>
      {children}
    </ContentContext.Provider>
  );
}

export function useContent(): ContentContextValue {
  const ctx = useContext(ContentContext);
  if (!ctx) {
    throw new Error('useContent must be used within a ContentProvider');
  }
  return ctx;
}